import React from "react";
import {
  Autocomplete,
  Box,
  Button,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import QrCodeScannerRoundedIcon from "@mui/icons-material/QrCodeScannerRounded";
import ItemOperationalFields from "./ItemOperationalFields";

const BRAND = { dark: "var(--wp-panel-text-primary, #19242F)" };

function same(left, right) {
  return String(left || "").trim().toLocaleLowerCase("es") === String(right || "").trim().toLocaleLowerCase("es");
}

export default function GastronomicAdvancedEditor({
  form,
  setForm,
  categories = [],
  subcategories = [],
  onCategoryChange,
  canManageCosts = false,
  canManageAccounting = false,
  compact = false,
  fieldErrors = {},
  editing = false,
  onOpenScanner,
  onDownloadCode,
  canDownloadCode = false,
}) {
  const update = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const addAttribute = () => setForm((current) => ({ ...current, attributes: [...(current.attributes || []), { label: "", value: "" }].slice(0, 30) }));
  const updateAttribute = (index, key, value) => setForm((current) => ({ ...current, attributes: (current.attributes || []).map((row, rowIndex) => rowIndex === index ? { ...row, [key]: value } : row) }));
  const removeAttribute = (index) => setForm((current) => ({ ...current, attributes: (current.attributes || []).filter((_, rowIndex) => rowIndex !== index) }));

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 1.6, borderRadius: 3 }}>
        <Stack spacing={1.4}>
          <Box>
            <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>Clasificación y códigos</Typography>
            <Typography variant="body2" color="text.secondary">La Sección ordena la Carta; esta categoría es la clasificación general del ERP y puede mantenerse igual o ser distinta.</Typography>
          </Box>
          <Autocomplete
            freeSolo
            autoHighlight
            options={categories}
            value={form.category || null}
            inputValue={form.category || ""}
            onChange={(_event, value) => onCategoryChange(String(value || ""))}
            onInputChange={(_event, value, reason) => { if (reason !== "reset") onCategoryChange(value); }}
            renderInput={(params) => <TextField {...params} label="Categoría general del ítem" required error={Boolean(fieldErrors.category)} helperText={fieldErrors.category || "Usala si necesitás una clasificación ERP distinta de la Sección de la Carta."} />}
          />
          <Autocomplete
            freeSolo
            autoHighlight
            options={subcategories}
            value={form.subcategory || null}
            inputValue={form.subcategory || ""}
            onChange={(_event, value) => update("subcategory", String(value || ""))}
            onInputChange={(_event, value, reason) => { if (reason !== "reset") update("subcategory", value); }}
            renderInput={(params) => <TextField {...params} label="Subcategoría" helperText="Segundo nivel opcional. Ej.: Bebidas → Cervezas / Vinos." />}
          />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
            <TextField label="Código interno / SKU" value={form.code || ""} onChange={(event) => update("code", event.target.value)} helperText={editing ? "Se conserva al editar otros datos." : "Si queda vacío, WebProfit puede generar un código interno al crear."} fullWidth />
            <TextField label="Unidad" value={form.unit || ""} onChange={(event) => update("unit", event.target.value)} placeholder="unidad, kg, litro..." fullWidth />
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={0.8}>
            {!editing && onOpenScanner ? <Button type="button" size="small" variant="outlined" startIcon={<QrCodeScannerRoundedIcon />} onClick={onOpenScanner} sx={{ textTransform: "none", fontWeight: 850 }}>Escanear etiqueta</Button> : null}
            {canDownloadCode && onDownloadCode ? <Button type="button" size="small" startIcon={<DownloadRoundedIcon />} onClick={onDownloadCode} sx={{ textTransform: "none", fontWeight: 850 }}>Descargar código</Button> : null}
          </Stack>
        </Stack>
      </Paper>

      <ItemOperationalFields form={form} setForm={setForm} canManageCosts={canManageCosts} canManageAccounting={canManageAccounting} compact={compact} />

      <Paper variant="outlined" sx={{ p: 1.6, borderRadius: 3 }}>
        <Stack spacing={1.4}>
          <TextField label="Etiquetas" value={form.tags || ""} onChange={(event) => update("tags", event.target.value)} placeholder="Ej.: premium, temporada" helperText="Separalas con comas. Son datos auxiliares del ítem." fullWidth />
          <Divider />
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1} alignItems={{ sm: "center" }}>
            <Box>
              <Typography sx={{ fontWeight: 900, color: BRAND.dark }}>Datos adicionales</Typography>
              <Typography variant="body2" color="text.secondary">Campos libres que ya formen parte de tu operación.</Typography>
            </Box>
            <Button type="button" startIcon={<AddCircleOutlineRoundedIcon />} onClick={addAttribute} sx={{ textTransform: "none", fontWeight: 850 }}>Agregar dato</Button>
          </Stack>
          {(form.attributes || []).map((row, index) => (
            <Stack key={`gastronomic-attribute-${index}`} direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
              <TextField label="Dato" value={row.label || ""} onChange={(event) => updateAttribute(index, "label", event.target.value)} fullWidth />
              <TextField label="Valor" value={row.value || ""} onChange={(event) => updateAttribute(index, "value", event.target.value)} fullWidth />
              <IconButton type="button" onClick={() => removeAttribute(index)} aria-label={`Eliminar dato ${index + 1}`} color="error"><DeleteRoundedIcon /></IconButton>
            </Stack>
          ))}
        </Stack>
      </Paper>
    </Stack>
  );
}
